<template>
  <div>
    <div ref="editor"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineExpose } from 'vue'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'

// Props y Emits para v-model
const props = defineProps({
  modelValue: { type: String, default: '' }
})
const emit = defineEmits(['update:modelValue'])

// Registrar fuentes personalizadas
const Font = Quill.import('formats/font')
Font.whitelist = [
  'arial', 
  'times-new-roman', 
  'courier-new', 
  'roboto',
  'georgia',
  'verdana'
]
Quill.register(Font, true)

// Tamaño de fuente personalizado
const Size = Quill.import('formats/size')
Size.whitelist = ['small', false, 'large', 'huge']
Quill.register(Size, true)

const editor = ref(null)
let quill

onMounted(() => {
  quill = new Quill(editor.value, {
    theme: 'snow',
    modules: {
      toolbar: {
        container: [
          [{ 'font': ['arial', 'times-new-roman', 'courier-new', 'roboto', 'georgia', 'verdana'] }],
          [{ 'size': Size.whitelist }],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'script': 'sub' }, { 'script': 'super' }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'align': [] }],
          ['blockquote', 'code-block'],
          ['link', 'image', 'video'],
          ['clean']
        ],
        handlers: {
          image: () => handleUpload('image'),
          video: () => handleUpload('video')
        }
      }
    }
  })

  // Cargar contenido inicial
  if (props.modelValue) {
    quill.clipboard.dangerouslyPasteHTML(props.modelValue)
  }

  // Emitir cambios al padre
  quill.on('text-change', () => {
    emit('update:modelValue', quill.root.innerHTML)
  })
})

// 🔄 Sincronización con cambios posteriores en modelValue
watch(() => props.modelValue, (newVal) => {
  if (newVal !== quill.root.innerHTML) {
    quill.clipboard.dangerouslyPasteHTML(newVal || '')
  }
})

// Subida de archivos personalizada
async function handleUpload(type) {
  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', type === 'image' ? 'image/*' : 'video/*')
  input.click()

  input.onchange = async () => {
    const file = input.files[0]
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch('/api/upload', {
      method: 'POST',
      body: formData
    })

    const data = await response.json()
    const url = data.url
    const range = quill.getSelection()

    if (type === 'image') {
      quill.insertEmbed(range.index, 'image', url)
    } else if (type === 'video') {
      quill.insertEmbed(range.index, 'video', url)
    }
  }
}

// Método opcional por si lo necesitas
function getHTML() {
  return quill.root.innerHTML
}

defineExpose({ getHTML })
</script>

<style scoped>
/* Fuentes personalizadas */

.ql-font-arial {
  font-family: Arial, sans-serif;
}

.ql-font-times-new-roman {
  font-family: "Times New Roman", serif;
}

.ql-font-courier-new {
  font-family: "Courier New", monospace;
}

.ql-font-roboto {
  font-family: Roboto, sans-serif;
}

.ql-font-georgia {
  font-family: Georgia, serif;
}

.ql-font-verdana {
  font-family: Verdana, sans-serif;
}
</style>
